// src/pages/ManWithVan.jsx
import React, { useState, useEffect , useRef} from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { track } from '../utils/track';
import Testimonials from '../components/Testimonials';
import {
  ShieldCheck, MapPin, Clock, CheckCircle, Info, Phone, MessageCircle, Mail, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import styled from 'styled-components';
import {
  Hero, HeroContent, HeroBox, HeroTitle, HeroSubtitle, HeroButtons,
  CTAButton, CTAButtonTwo,
  Section, SectionTitle, SectionSubtitle,
  ServiceCard, ServiceIcon, ServiceTitle, ServiceDescription,
  HowItWorksSection, HowItWorksContainer, StepsGrid, StepCard, StepNumber, StepH3, StepDescription,
  CTASection, CTAContent, CTATitle, CTAText,
} from '../styles';

const PHONE_DISPLAY = '07436 117842';
const PHONE_TEL = '+447436117842';
const WHATSAPP_NUMBER = '447436117842';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi, I'd like a quote for a man & van job.")}`;

const WhatWeMoveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 3rem;
`;

const ActionPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 1.75rem;
  border-radius: 8px;
  background: #00A8E8;
  color: #fff;
  border: 2px solid #00A8E8;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s ease;
  cursor: pointer;
  flex: 1;
  max-width: 250px;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ActionSecondary = styled(ActionPrimary)`
  background: transparent;
  color: #10243D;
  border: 1px solid #cbd5e1;
  /* Adjust padding slightly to offset the 1px border vs the primary button's 2px border */
  padding: calc(0.9rem + 1px) 1.75rem;
`;

const InsuranceBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #001F3D, #003366);
  color: #fff;
  padding: 1rem 2rem;
  font-weight: 600;
  font-size: 1.05rem;
  text-align: center;
  flex-wrap: wrap;

  svg {
    flex-shrink: 0;
  }

  @media (max-width: 560px) {
    font-size: 0.95rem;
    padding: 0.9rem 1.25rem;
  }
`;

const NoteCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  max-width: 760px;
  margin: 3.5rem auto 0;
  padding: 1.5rem 1.75rem;
  background: #f0f4f8;
  border-left: 4px solid #001F3D;
  border-radius: 12px;
  text-align: left;

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 0.9rem;
    padding: 1.25rem;
  }
`;

const NoteIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #001F3D;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NoteTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #001F3D;
`;

const NoteText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #334155;
`;

const QuoteForm = styled.form`
  display: grid;
  gap: 1rem;
  max-width: 520px;
  margin: 2rem auto 0;
  text-align: left;
`;

const Field = styled.label`
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #10243D;
  min-width: 0; /* Forces grid cell to respect boundaries */

  input,
  textarea {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    color: #1a1a1a;
    font: inherit;
    font-weight: 400;
    box-sizing: border-box;
  }

  input:focus,
  textarea:focus {
    outline: 3px solid #4da3ff;
    outline-offset: 1px;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${({ $error }) => ($error ? '#fca5a5' : '#86efac')};
`;

const SubmitButton = styled.button`
  padding: 1rem 1.5rem;
  border: 2px solid #001F3D;
  border-radius: 8px;
  background: #001F3D;
  color: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #003366;
  }

  &:focus-visible {
    outline: 3px solid #4da3ff;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeroSubmitButton = styled(SubmitButton)`
  background: #00A8E8;
  border-color: #00A8E8;
  width: auto;
  margin-top: 0;

  &:hover:not(:disabled) {
    background: #0096d1;
  }
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(16, 36, 61, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2.5rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 560px) {
    padding: 1.5rem;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #0f172a;
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.5rem;
  color: #10243D;
  font-size: 1.75rem;
  text-align: center;
`;


const recentMoves = [
  { id: 1, title: 'Furniture Move', from: 'Basingstoke', to: 'Basingstoke', cost: '60', saved: '22', img: '/customer1.webp', completionTime: '1 hour' },
  { id: 2, title: 'Home Removal', from: "Canning Town", to: 'Canary Wharf', cost: '640', saved: '377', img: '/customer6.webp', completionTime: '6 hours' },
  { id: 3, title: 'Flat Move & Storage', from: 'Canning Town', to: 'Croydon', cost: '450', saved: '45', img: '/customer8.webp', completionTime: '6 hours' },
  { id: 4, title: 'Furniture Move', from: 'Portsmouth', to: 'Portsmouth', cost: '45', saved: '80', img: '/customer7.webp', completionTime: '1 hour' },
];

const services = [
  {
    img: '/sofa_new.webp',
    title: 'House & Flat Moves',
    desc: 'Studios to large family homes, plus university halls, single rooms and small flat shares. A driver and helper to load, transport and unload, with extra crew for bigger moves.',
  },
  {
    img: '/sofa_move.webp',
    title: 'Single Item & Furniture Collection',
    desc: 'Sofas, wardrobes, beds and furniture from shops, auctions, Facebook Marketplace or eBay.',
  },
  {
    img: '/waste_removal.webp',
    title: 'Waste & House Clearance',
    desc: 'Old furniture, appliances and household waste collected and disposed of responsibly.',
  },
  {
    img: '/commercial_business.webp',
    title: 'Commercial Moves',
    desc: 'Require an ongoing partner to facilitate deliveries or an Office Removal? We are proud to provide a range of commercial services.',
  },
];

const trustPoints = [
  { icon: MapPin, label: 'Hampshire-based' },
  { icon: Clock, label: 'Quotes usually within the hour' },
  { icon: Clock, label: 'We operate 24/7' },
  { icon: CheckCircle, label: 'Fixed price agreed upfront' },
];

const ManWithVan = () => {
  const initialForm = {
    name: '',
    email: '',
    phone: '',
    moveDate: '',
    pickup: '',
    dropoff: '',
    details: '',
  };
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noDate, setNoDate] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);
  
  const [headerH, setHeaderH] = useState(110);
  useEffect(() => {
    const measure = () => {
      const h = document.querySelector('header')?.parentElement?.offsetHeight;
      if (h) setHeaderH(h);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleNoDateChange = (e) => {
    const isChecked = e.target.checked;
    setNoDate(isChecked);
    if (isChecked) {
      setForm(prev => ({ ...prev, moveDate: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');

    const templateParams = {
      enquiryType: 'Man with a Van (landing page)',
      name: form.name,
      email: form.email,
      phone: form.phone,
      moveDate: noDate ? 'Not decided yet' : form.moveDate,
      pickup: form.pickup,
      dropoff: form.dropoff,
      message: form.details,
      time: new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        track('generate_lead', { form_name: 'Man with a Van landing page' });
        setStatus('sent');
        setForm(initialForm);
        setNoDate(false);
      })
      .catch(() => setStatus('error'));
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setStatus('idle');
  };


  const carouselRef = useRef(null);
  const videoRef = useRef(null);

  const pauseVideo = () => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  };

  // Pause video if the user scrolls the page vertically
  useEffect(() => {
    window.addEventListener('scroll', pauseVideo);
    return () => window.removeEventListener('scroll', pauseVideo);
  }, []);

  const scrollLeft = () => {
    pauseVideo();
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -320, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    pauseVideo();
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 320, behavior: 'smooth' });
    }
  };

  return (
    <>
      <style>{`
        input[type="date"] {
          -webkit-appearance: none;
          appearance: none;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
        }
        @media (max-width: 560px) {
          input[type="date"] {
            font-size: 16px !important; /* Prevents iOS from zooming in */
          }
        }
      `}</style>
      {/* HERO */}
      <Hero
        style={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'border-box',
          paddingTop: headerH + 32,
          paddingBottom: 0,
          paddingRight: 0,
          paddingLeft: '5%',
          background: '#10243D',
          overflow: 'hidden',
        }}
      >
        <HeroContent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          style={{ flex: '1 1 480px', maxWidth: '720px', margin: 0, minWidth: 0, paddingRight: '2rem', paddingBottom: '32px', zIndex: 1 }}
        >
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <HeroTitle style={{ fontSize: '3rem', color: '#fff', marginBottom: '1rem' }}>Man &amp; Van Services</HeroTitle>
            <HeroSubtitle style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '2rem' }}>
              Moving, collecting or clearing out? We've got you covered.
            </HeroSubtitle>
            
            <HeroBox style={{ background: '#fff', padding: '2rem', borderRadius: '16px' }}>
              <form onSubmit={(e) => { e.preventDefault(); setIsModalOpen(true); }}>
                <FieldRow>
                  <Field>Pickup location*<input name="pickup" type="text" placeholder="e.g. SO16 4AA" required value={form.pickup} onChange={handleChange} /></Field>
                  <Field>Drop-off location*<input name="dropoff" type="text" placeholder="e.g. PO1 2AB" required value={form.dropoff} onChange={handleChange} /></Field>
                </FieldRow>
                
                <FieldRow style={{ alignItems: 'start' }}>
                  <Field>
                    Move date
                    <input
                      name="moveDate"
                      type="date"
                      value={form.moveDate}
                      onChange={handleChange}
                      disabled={noDate}
                      style={{ opacity: noDate ? 0.5 : 1, backgroundColor: noDate ? '#f1f5f9' : '#fff' }}
                    />
                  </Field>
                  <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: '1.6rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10243D', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={noDate}
                        onChange={handleNoDateChange}
                        style={{ width: '1.1rem', height: '1.1rem', margin: 0, cursor: 'pointer' }}
                      />
                      I don't have a move date yet
                    </label>
                  </div>
                </FieldRow>

                <Field>What needs moving?*<textarea name="details" rows={2} placeholder="e.g. 2-seater sofa, double bed and 6 boxes" required value={form.details} onChange={handleChange} /></Field>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <img src="/google_favicon.webp" alt="Google" style={{ width: '18px', height: '18px' }} />
                    <span style={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.9rem' }}>5.0</span>
                    <span style={{ color: '#fbbc04', fontSize: '1.1rem', letterSpacing: '1px', marginTop: '-2px' }}>★★★★★</span>
                  </div>
                  <HeroSubmitButton type="submit">
                    Get a free quote
                  </HeroSubmitButton>
                </div>
              </form>
            </HeroBox>
          </motion.div>
        </HeroContent>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            flex: '1 1 450px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            minWidth: 0,
            alignSelf: 'stretch',
            pointerEvents: 'none',
            marginTop: 'auto',
          }}
        >
          <img
            src="/usl_right3.webp"
            alt="Urgent Logistics man and van service"
            style={{
              width: '100%',
              height: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              objectPosition: 'bottom right',
              display: 'block',
            }}
          />
        </motion.div>
      </Hero>

      {/* INSURANCE BANNER */}
      <InsuranceBanner>
        <ShieldCheck size={22} />
        Fully insured — your goods are covered in transit
      </InsuranceBanner>

      {/* TRUST BAR */}
      <div style={{ background: 'white', padding: '2.5rem 5%' }}>
        <div style={{
          maxWidth: '1400px', margin: '0 auto',
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: '2rem',
        }}>
          {trustPoints.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '10px',
                  background: '#f0f4f8', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={22} color="#001F3D" />
                </div>
                <span style={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>{t.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      
      {/* RECENT MOVES & REVIEWS (NEW SECTION) */}
      <div style={{ background: '#F8F7F4', padding: '5rem 0', overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4rem',
          alignItems: 'flex-start',
          // This aligns the left side perfectly, but allows the right side to bleed off the screen edge
          paddingLeft: 'max(5%, calc((100vw - 1400px) / 2))',
          width: '100%'
        }}>
          {/* Left Column: Merged Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ flex: '1 1 400px', maxWidth: '500px', paddingLeft: '1rem', paddingRight: '2rem', paddingTop: '1.5rem' }}
          >
            <h2 style={{ fontSize: '2.5rem', color: '#10243D', marginBottom: '1.5rem', lineHeight: '1.2', fontWeight: '800' }}>
              Recent Moves &amp; Reviews
            </h2>
<p style={{ fontSize: '1.1rem', color: '#10243D', marginBottom: '1.25rem', lineHeight: '1.7' }}>
              From local furniture pickups to long-distance relocations, we handle jobs of all sizes. Our transparent pricing means you know exactly what you&apos;re paying upfront no hidden fees, just great service.
            </p>
            <p style={{ fontSize: '1.1rem', color: '#10243D', marginBottom: 0, lineHeight: '1.7' }}>
              Available 24/7, we work around your schedule. Whether you need a last-minute office clearance or secure storage during a house move, our team is ready to help whenever you are.
            </p>
          </motion.div>

          {/* Right Column: Carousel (Bleeds off the screen) */}
          <div style={{ flex: '2 1 700px', minWidth: 0 }}>
            <div
              ref={carouselRef}
              onScroll={pauseVideo}
              style={{
                display: 'flex',
                gap: '1.5rem',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                paddingBottom: '1.5rem',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                paddingRight: '5%',
              }}
            >
              {/* Video Testimonial */}
              <div
                style={{
                  minWidth: '340px',
                  flex: '0 0 auto',
                  background: '#000',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  scrollSnapAlign: 'start',
                  position: 'relative', // Crucial: allows absolute positioning of video
                  minHeight: '420px'    // Gives it a base height if it renders before cards
                }}
              >
                <video
                  ref={videoRef}
                  src="/customer_new.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  style={{
                    position: 'absolute', // Takes up 0 space in flex layout, stops card squishing
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {recentMoves.map((move) => (
                <div
                  key={move.id}
                  style={{
                    minWidth: '340px', // Thick cards to show ~2.3 at a time
                    flex: '0 0 auto',
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    scrollSnapAlign: 'start',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Image Area */}
                  <div style={{ padding: '1rem 1rem 0 1rem' }}>
                    <img 
                      src={move.img} 
                      alt={move.title} 
                      style={{ 
                        width: '100%', 
                        height: '200px',
                        objectFit: 'cover', 
                        borderRadius: '12px', 
                        border: '1px solid #e2e8f0',
                        display: 'block'
                      }} 
                    />
                  </div>
                  
                  {/* Content Area (Vertically Centered) */}
                  <div style={{ 
                    padding: '1.5rem', 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center' 
                  }}>
                    <h3 style={{ fontSize: '1.4rem', color: '#1a1a1a', marginBottom: '1rem', fontWeight: '700' }}>
                      {move.title}
                    </h3>
                    <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '1.05rem' }}>From: <strong style={{ color: '#1a1a1a' }}>{move.from}</strong></p>
                    <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '1.05rem' }}>To: <strong style={{ color: '#1a1a1a' }}>{move.to}</strong></p>
                    <p style={{ margin: "0 0 0.5rem", color: "#475569", fontSize: "1.05rem" }}>Completion Time: <strong style={{ color: "#1a1a1a" }}>{move.completionTime}</strong></p>
                    <p style={{ margin: 0, color: '#475569', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.05rem' }}>
                      Review: <span style={{ color: '#fbbc04', fontSize: '1.2rem', letterSpacing: '2px' }}>★★★★★</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Carousel Controls */}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-start', paddingLeft: '1rem' }}>
              <button
                onClick={scrollLeft}
                style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10243D', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={scrollRight}
                style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10243D', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WHAT WE MOVE */}
      <Section services id="what-we-move" style={{ background: '#fff' }}>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a' }}
        >
          What We Move
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a' }}
        >
          From single items to full house moves. If it fits in a van, we can help.
        </SectionSubtitle>

        <WhatWeMoveGrid>
          {services.map((s, i) => {
            return (
              <ServiceCard
                key={s.title}
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                style={{ boxShadow: 'none', border: '1px solid #cbd5e1', padding: '1.5rem', paddingBottom: '1.25rem' }}
              >
                <ServiceIcon style={{ background: 'transparent', padding: 0, overflow: 'hidden', width: '85%', height: '140px', margin: '0 auto 0.5rem auto' }}>
                  <img 
                    src={s.img} 
                    alt={s.title} 
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} 
                  />
                </ServiceIcon>
                <ServiceTitle style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1a1a1a', marginTop: 0, marginBottom: '0.75rem' }}>{s.title}</ServiceTitle>
                <ServiceDescription>{s.desc}</ServiceDescription>
              </ServiceCard>
            );
          })}
        </WhatWeMoveGrid>

        <style>{`
          /* Full-width What We Move buttons on mobile only */
          @media (max-width: 768px) {
            .usl-mwv-action-buttons {
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 12px !important;
              width: 100% !important;
              max-width: 100% !important;
            }

            .usl-mwv-action-buttons > a {
              display: flex !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              flex: 0 0 auto !important;
              align-items: center !important;
              justify-content: center !important;
              text-align: center;
              min-height: 56px;
            }
          }
        `}</style>

        <ActionRow className="usl-mwv-action-buttons">
          <ActionPrimary href="#" onClick={(e) => { e.preventDefault(); setIsModalOpen(true); }}>Get a Free Quote</ActionPrimary>
          <ActionSecondary href={`tel:${PHONE_TEL}`}>
            <Phone size={18} /> Leave us a call
          </ActionSecondary>
        </ActionRow>
      </Section>

      <Testimonials />

      {/* HOW IT WORKS */}
      <HowItWorksSection>
        <HowItWorksContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ color: '#1a1a1a' }}
          >
            How It Works
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ color: '#1a1a1a' }}
          >
            
          </SectionSubtitle>

          <StepsGrid>
            <StepCard style={{ boxShadow: 'none'}} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <StepNumber style={{ boxShadow: 'none' }}>1</StepNumber>
              <StepH3>Tell Us the Job</StepH3>
              <StepDescription>Contact us with what you need moved, and pickup/drop-off locations.</StepDescription>
            </StepCard>
            <StepCard style={{ boxShadow: 'none'}} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <StepNumber style={{ boxShadow: 'none' }}>2</StepNumber>
              <StepH3>Get a Bespoke Price</StepH3>
              <StepDescription>We&rsquo;ll come back to you with a fair, fixed price based on your job.</StepDescription>
            </StepCard>
            <StepCard style={{ boxShadow: 'none'}} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <StepNumber style={{ boxShadow: 'none' }}>3</StepNumber>
              <StepH3>Book a Time</StepH3>
              <StepDescription>Choose a date and time that works, including weekends.</StepDescription>
            </StepCard>
            <StepCard style={{ boxShadow: 'none'}} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <StepNumber style={{ boxShadow: 'none' }}>4</StepNumber>
              <StepH3>Job Done</StepH3>
              <StepDescription>Our driver and helper handle the loading, transport and unloading.</StepDescription>
            </StepCard>
          </StepsGrid>

          
        </HowItWorksContainer>
      </HowItWorksSection>

      
      {/* AREAS WE SERVE */}
      <Section id="areas-we-serve" style={{ background: '#F8F7F4' }}>
        <style>{`
          @media (max-width: 768px) {
            .area-pill.hidden-mobile { display: none; }
            .area-row { flex-direction: column !important; align-items: center !important; text-align: center; }
            .area-img { margin: 0 auto 1rem auto !important; width: 100% !important; max-width: 300px !important; height: auto !important; aspect-ratio: 1/1; }
            .area-content { text-align: center; }
            .area-content .see-more-btn { margin: 0.5rem auto 0 auto !important; display: block; }
          }
          @media (min-width: 769px) {
            .see-more-btn { display: none !important; }
          }
        `}</style>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a', marginBottom: '0.5rem' }}
        >
          Areas We Serve
        </SectionTitle>
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#1a1a1a', marginBottom: '2rem' }}
        >
          Covering every town and village across Hampshire, Guildford &amp; Surrey, and London. If you don&apos;t see your area, give us a message anyway.
        </SectionSubtitle>

        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '2rem', 
          maxWidth: '1200px', 
          margin: '0 auto' 
        }}>
          {[
            { 
              name: 'Hampshire', 
              img: '/customer4.webp',
              text: 'Covering every town and village across the entire county, including:',
              towns: ['Portsmouth', 'Southampton', 'Basingstoke', 'Andover', 'Winchester', 'Farnborough', 'Aldershot', 'Eastleigh', 'Havant', 'Fareham', 'Gosport', 'Waterlooville', 'Fleet', 'Petersfield', 'Romsey', 'Lymington', 'Bishop\'s Waltham', 'Totton'] 
            },
            { 
              name: 'Guildford & Surrey', 
              img: '/customer5.webp',
              text: 'Covering every town and village in and around Surrey, including:',
              towns: ['Guildford', 'Woking', 'Godalming', 'Farnham', 'Haslemere', 'Cranleigh', 'Epsom', 'Camberley', 'Staines', 'Weybridge', 'Leatherhead', 'Dorking', 'Reigate', 'Redhill', 'Oxted', 'Caterham'] 
            },
            { 
              name: 'London', 
              img: '/customer3.webp',
              text: 'Covering every borough and area across Greater London, including:',
              towns: ['Central London', 'Croydon', 'Kingston upon Thames', 'Richmond', 'Bromley', 'Ealing', 'Wimbledon', 'Ilford', 'Romford', 'Enfield', 'Barnet', 'Harrow', 'Uxbridge', 'Hounslow', 'Sutton', 'Wembley'] 
            }
          ].map((area, index) => (
            <div key={area.name} style={{ padding: '2.5rem 0', borderBottom: index !== 2 ? '1px solid #10243D' : 'none' }}>
              
              {/* Flex row to keep image on left and content on right */}
              <div className="area-row" style={{ display: 'flex', flexDirection: 'row', gap: '2rem', alignItems: 'flex-start' }}>
                
                {/* Square Image on the left */}
                <img 
                  className="area-img"
                  src={area.img} 
                  alt={area.name} 
                  style={{ 
                    width: '180px', 
                    height: '180px', 
                    objectFit: 'cover', 
                    borderRadius: '16px', 
                    flexShrink: 0 
                  }} 
                />

                {/* Content on the right */}
                <div className="area-content" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.5rem', color: '#10243D', margin: 0, marginBottom: '0.4rem', fontWeight: '700', lineHeight: '1.2' }}>{area.name}</h3>
                    <p style={{ color: '#10243D', margin: 0, lineHeight: '1.6', fontWeight: '500' }}>
                      {area.text}
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                    {area.towns.map((city, i) => {
                      const isHidden = i >= 11 && expandedArea !== area.name;
                      return (
                        <span 
                          key={city} 
                          className={isHidden ? 'area-pill hidden-mobile' : 'area-pill'} 
                          style={{ background: '#fff', border: '1px solid #cbd5e1', color: '#10243D', padding: '0.4rem 0.8rem', borderRadius: '50px', fontSize: '0.85rem', fontWeight: '600', whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {city}
                        </span>
                      );
                    })}
                    
                    <button 
                      type="button"
                      onClick={() => setExpandedArea(expandedArea === area.name ? null : area.name)}
                      className="see-more-btn"
                      style={{ 
                        background: 'transparent', 
                        border: 'none', 
                        color: '#00A8E8', 
                        fontWeight: '700', 
                        cursor: 'pointer', 
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.4rem 0.5rem',
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      {expandedArea === area.name ? 'See less' : 'See more...'}
                    </button>
                  </div>
                </div>
              </div>
              
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <CTASection id="quote" style={{ background: '#fff' }}>
        <CTAContent initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <CTATitle style={{ color: '#10243D' }}>Get your quote</CTATitle>
          <CTAText style={{ color: '#475569' }}>Tell us about the job and we&rsquo;ll match or beat any quote. We&rsquo;re open 24/7.</CTAText>

          <div style={{ maxWidth: '700px', margin: '2rem auto', textAlign: 'left' }}>
            <QuoteForm onSubmit={handleSubmit} style={{ margin: 0, maxWidth: '100%' }}>
              
              
              {/* Personal Details (Top) */}
              <FieldRow>
                <Field style={{ color: '#10243D' }}>Name*<input name="name" type="text" autoComplete="name" required value={form.name} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
                <Field style={{ color: '#10243D' }}>Email*<input name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
              </FieldRow>
              <Field style={{ color: '#10243D' }}>Phone number*<input name="phone" type="tel" autoComplete="tel" required value={form.phone} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.5rem 0', paddingTop: '1rem' }}></div>

              {/* Job Details (Prefilled from Hero) */}
              <FieldRow>
                <Field style={{ color: '#10243D' }}>Pickup location*<input name="pickup" type="text" required value={form.pickup} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
                <Field style={{ color: '#10243D' }}>Drop-off location*<input name="dropoff" type="text" required value={form.dropoff} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
              </FieldRow>

              <FieldRow style={{ alignItems: 'start' }}>
                <Field style={{ color: '#10243D' }}>
                  Move date
                  <input 
                    name="moveDate" 
                    type="date" 
                    value={form.moveDate} 
                    onChange={handleChange} 
                    disabled={noDate} 
                    style={{ border: '1px solid #cbd5e1', opacity: noDate ? 0.5 : 1, backgroundColor: noDate ? '#f1f5f9' : '#fff' }} 
                  />
                </Field>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: '1.6rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10243D', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={noDate} 
                      onChange={handleNoDateChange} 
                      style={{ width: '1.1rem', height: '1.1rem', margin: 0, cursor: 'pointer' }} 
                    />
                    I don't have a move date yet
                  </label>
                </div>
              </FieldRow>

              <Field style={{ color: '#10243D' }}>What needs moving?*<textarea name="details" rows={2} required value={form.details} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>

              <div style={{ marginTop: '1rem' }}>
                <HeroSubmitButton type="submit" disabled={status === 'sending'} style={{ width: '100%' }}>
                  {status === 'sending' ? 'Sending…' : 'Send quote request'}
                </HeroSubmitButton>
              </div>
              
              {status === 'error' && <StatusText $error style={{ marginTop: '1rem' }}>Something went wrong. Please try again, or WhatsApp or call us.</StatusText>}
              {status === 'sent' && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <StatusText style={{ color: '#10243D', fontSize: '1rem', fontWeight: 600, display: 'block', marginBottom: '1rem' }}>
                    Thanks, we&rsquo;ve got your request and will be in touch shortly.
                  </StatusText>
                  <button type="button" onClick={handleCloseModal} style={{ padding: '0.75rem 1.5rem', background: '#10243D', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                    Close
                  </button>
                </div>
              )}
            </QuoteForm>
          </div>

          <ContactRow style={{ marginTop: '2rem' }}>
            <ContactLink href="mailto:info@urgentservicelogistics.co.uk" style={{ color: '#10243D' }}>
              <Mail size={18} /> info@urgentservicelogistics.co.uk
            </ContactLink>
            <ContactLink href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" style={{ color: '#10243D' }}>
              <MessageCircle size={18} /> WhatsApp us
            </ContactLink>
            <ContactLink href={`tel:${PHONE_TEL}`} style={{ color: '#10243D' }}>
              <Phone size={18} /> Call {PHONE_DISPLAY}
            </ContactLink>
          </ContactRow>
        </CTAContent>
      </CTASection>

      {/* QUOTE MODAL */}
      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={handleCloseModal}>
              <X size={24} />
            </ModalClose>
            <ModalTitle>Complete your quote request</ModalTitle>
            
            <QuoteForm onSubmit={handleSubmit} style={{ margin: 0, maxWidth: '100%' }}>
              
              
              {/* Personal Details (Top) */}
              <FieldRow>
                <Field style={{ color: '#10243D' }}>Name*<input name="name" type="text" autoComplete="name" required value={form.name} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
                <Field style={{ color: '#10243D' }}>Email*<input name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
              </FieldRow>
              <Field style={{ color: '#10243D' }}>Phone number*<input name="phone" type="tel" autoComplete="tel" required value={form.phone} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>

              {/* Divider */}
              <div style={{ borderTop: '1px solid #e2e8f0', margin: '0.5rem 0', paddingTop: '1rem' }}></div>

              {/* Job Details (Prefilled from Hero) */}
              <FieldRow>
                <Field style={{ color: '#10243D' }}>Pickup location*<input name="pickup" type="text" required value={form.pickup} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
                <Field style={{ color: '#10243D' }}>Drop-off location*<input name="dropoff" type="text" required value={form.dropoff} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>
              </FieldRow>

              <FieldRow style={{ alignItems: 'start' }}>
                <Field style={{ color: '#10243D' }}>
                  Move date
                  <input 
                    name="moveDate" 
                    type="date" 
                    value={form.moveDate} 
                    onChange={handleChange} 
                    disabled={noDate} 
                    style={{ border: '1px solid #cbd5e1', opacity: noDate ? 0.5 : 1, backgroundColor: noDate ? '#f1f5f9' : '#fff' }} 
                  />
                </Field>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: '1.6rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10243D', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={noDate} 
                      onChange={handleNoDateChange} 
                      style={{ width: '1.1rem', height: '1.1rem', margin: 0, cursor: 'pointer' }} 
                    />
                    I don't have a move date yet
                  </label>
                </div>
              </FieldRow>

              <Field style={{ color: '#10243D' }}>What needs moving?*<textarea name="details" rows={2} required value={form.details} onChange={handleChange} style={{ border: '1px solid #cbd5e1' }} /></Field>

              <div style={{ marginTop: '1rem' }}>
                <HeroSubmitButton type="submit" disabled={status === 'sending'} style={{ width: '100%' }}>
                  {status === 'sending' ? 'Sending…' : 'Send quote request'}
                </HeroSubmitButton>
              </div>
              
              {status === 'error' && <StatusText $error style={{ marginTop: '1rem' }}>Something went wrong. Please try again, or WhatsApp or call us.</StatusText>}
              {status === 'sent' && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <StatusText style={{ color: '#10243D', fontSize: '1rem', fontWeight: 600, display: 'block', marginBottom: '1rem' }}>
                    Thanks, we&rsquo;ve got your request and will be in touch shortly.
                  </StatusText>
                  <button type="button" onClick={handleCloseModal} style={{ padding: '0.75rem 1.5rem', background: '#10243D', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                    Close
                  </button>
                </div>
              )}
            </QuoteForm>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default ManWithVan;
